import { useCallback, useMemo, useRef } from 'react';

import { XApiVerbName } from '@hendrx/shared/xapi-events';
import { WidgetType } from 'shared/common/utils/widgetsUtils';
import { getElapsedSeconds, getElapsedTime } from 'shared/utils/Time.utils';

// eslint-disable-next-line boundaries/element-types
import { useEventData } from './useEventData';
// eslint-disable-next-line boundaries/element-types
import { sendEvent } from '../../../xApi/queries/sendEvent';

type MediaVerb = XApiVerbName.ExperiencedPlayed | XApiVerbName.ExperiencedPaused | XApiVerbName.ExperiencedStopped;

export function useMediaEvents(widgetId: string, widgetType: WidgetType) {
  const eventData = useEventData();
  const finalEventData = useMemo(
    () => ({ ...eventData, widgetId: widgetId ?? '', widgetType: widgetType ?? '' }),
    [eventData, widgetId, widgetType]
  );

  // YouTube reports PAUSED again when a seek lands while paused, so Paused is sent only after a Played.
  // Played is not deduplicated: a seek while playing reports it again with the new position, on purpose.
  const lastSentVerbRef = useRef<MediaVerb | null>(null);

  const sendMediaEvent = useCallback(
    (verb: MediaVerb, progressFraction: number, totalSeconds: number, src: string) => {
      lastSentVerbRef.current = verb;
      void sendEvent({
        eventType: 'media',
        verb,
        ...finalEventData,
        currentTime: getElapsedTime(progressFraction, totalSeconds),
        currentTimeInSeconds: getElapsedSeconds(progressFraction, totalSeconds),
        objectLink: src
      });
    },
    [finalEventData]
  );

  const sendMediaPlayedEvent = useCallback(
    (progressFraction: number, totalSeconds: number, src: string) => {
      sendMediaEvent(XApiVerbName.ExperiencedPlayed, progressFraction, totalSeconds, src);
    },
    [sendMediaEvent]
  );

  const sendMediaPausedEvent = useCallback(
    (progressFraction: number, totalSeconds: number, src: string) => {
      if (lastSentVerbRef.current !== XApiVerbName.ExperiencedPlayed) {
        return;
      }
      sendMediaEvent(XApiVerbName.ExperiencedPaused, progressFraction, totalSeconds, src);
    },
    [sendMediaEvent]
  );

  const sendMediaStoppedEvent = useCallback(
    (progressFraction: number, totalSeconds: number, src: string) => {
      sendMediaEvent(XApiVerbName.ExperiencedStopped, progressFraction, totalSeconds, src);
    },
    [sendMediaEvent]
  );

  return { sendMediaPlayedEvent, sendMediaPausedEvent, sendMediaStoppedEvent };
}
